import React, { useEffect } from 'react'
import {
    List,
    Datagrid,
    TextField as TextFieldRa,
    Create,
    SimpleForm,
    TextInput,
    EditButton,
    Show,
    Edit,
    SimpleShowLayout,
    SelectField,
    SelectInput,
    BooleanInput,
    BooleanField,
    Filter,
    SearchInput,
    useEditController,
    useInput,
} from 'react-admin'
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from '@material-ui/core'
import PersonIcon from '@material-ui/icons/Person'
import { required, minLength, email } from 'react-admin'
import api from '../../Services/Api'
export const UserIcon = PersonIcon

const validateEmail = [required(), email()]
const validadePassword = [required(), minLength(6)]
const lastName = [required(), minLength(1)]
const fistName = [required(), minLength(1)]

// Permissões agrupadas por categoria, para organizar a listagem nos seletores
const PERMISSION_GROUPS = [
    {
        group: 'Clientes',
        items: [{ id: 'Customer', name: 'Cliente final' }],
    },
    {
        group: 'Emissores de Seguros',
        items: [
            { id: 'assistanceleisure', name: 'Emissor de Seguros Lazer' },
            { id: 'assistancecorporate', name: 'Emissor de Seguros Corporativo' },
            { id: 'insuranceissuer', name: 'Emissor de Seguros B2C' },
        ],
    },
    {
        group: 'Administradores',
        items: [
            { id: 'assistanceadmin', name: 'Administrador Seguros' },
            {
                id: 'sysadmin',
                name: 'Administrador de Sistema (todas permissões)',
            },
        ],
    },
    {
        group: 'API',
        items: [
            { id: 'api', name: 'Usuário API' },            
        ],
    },
    {
        group: 'Gestão',
        items: [{ id: 'markupmanager', name: 'Gestor de Markup' }, { id: 'apimanager', name: 'Gestor API' },],
    },
]

// Filtra os grupos de permissões mantendo apenas os ids informados (preserva a ordem dos grupos)
const filterPermissionGroups = (ids) =>
    PERMISSION_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => ids.includes(item.id)),
    })).filter((group) => group.items.length > 0)

const permissionName = (groups, id) => {
    for (const group of groups) {
        const found = group.items.find((item) => item.id === id)
        if (found) return found.name
    }
    return id
}

// SelectInput/SelectArrayInput do react-admin não suportam agrupamento (optgroup) nativamente,
// então esse input customizado usa useInput + MUI Select para exibir as permissões por categoria
const GroupedSelectInput = ({ groups, label, source, multiple, ...rest }) => {
    const {
        input: { name, value, onChange },
        meta: { touched, error },
    } = useInput({ source, ...rest })

    const selected = multiple ? value || [] : value || ''

    return (
        <FormControl
            margin="dense"
            error={!!(touched && error)}
            style={{ minWidth: 300, marginBottom: 8 }}
        >
            <InputLabel htmlFor={name}>{label}</InputLabel>
            <Select
                id={name}
                multiple={multiple}
                value={selected}
                onChange={(event) => onChange(event.target.value)}
                renderValue={(value) =>
                    multiple
                        ? value.map((id) => permissionName(groups, id)).join(', ')
                        : permissionName(groups, value)
                }
            >
                {groups.flatMap((group) => [
                    <MenuItem
                        key={`group-${group.group}`}
                        disabled
                        style={{ fontWeight: 700, opacity: 1 }}
                    >
                        {group.group}
                    </MenuItem>,
                    ...group.items.map((item) => (
                        <MenuItem
                            key={item.id}
                            value={item.id}
                            style={{ paddingLeft: 32 }}
                        >
                            {item.name}
                        </MenuItem>
                    )),
                ])}
            </Select>
            {touched && error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
    )
}

const UserFilter = (props) => (
    <Filter {...props}>
        <SearchInput source="filter" alwaysOn />
        <TextInput label="Razão Social" source="corporateName" />
        <GroupedSelectInput
            label="Permissões"
            alwaysOn
            source="role"
            groups={filterPermissionGroups([
                'Customer',
                'assistanceadmin',
                'sysadmin',
                'api',
                'assistanceleisure',
                'assistancecorporate',
                'markupmanager',
            ])}
        />
    </Filter>
)

export const UserList = (props) => {
    return (
        <>
            <List
                {...props}
                exporter={false}
                title="Lista de Usuários"
                bulkActionButtons={false}
                sort={{ field: 'id', order: 'DESC' }}
                filters={<UserFilter />}
            >
                <Datagrid>
                    <TextFieldRa source="id" label="Código" />
                    <TextFieldRa source="firstName" label="Primeiro Nome" />
                    <TextFieldRa source="lastName" label="Último Nome" />
                    <TextFieldRa source="email" label="E-Mail" />
                    <TextFieldRa source="agentCode" label="Código Gate" />
                    <TextFieldRa source="roleText" label="Perfil" />
                    <TextFieldRa source="corporateName" label="Razão Social" />
                    <BooleanField source="active" label="Ativo" />
                    <EditButton label="" />
                </Datagrid>
            </List>
        </>
    )
}

export const UserCreate = (props) => {
    const [enterpriseList, setEnterpriseList] = React.useState([])

    useEffect(() => {
        ;(async () => {
            try {
                const results = await api.get(`admin/enterprises`, {
                    params: { issuer: true },
                })
                const empresasFormatted = results.data.map((result) => ({
                    id: result.id,
                    name: result.corporateName,
                }))
                setEnterpriseList(empresasFormatted)
            } catch (error) {
                console.log('Erro ao consultar empresas', error)
            }
        })()
    }, [])
    return (
        <>
            <Create {...props}>
                <SimpleForm>
                    <TextInput
                        label="Primeiro Nome"
                        source="firstName"
                        validate={fistName}
                    />
                    <TextInput
                        label="Último nome"
                        source="lastName"
                        validate={lastName}
                    />
                    <TextInput
                        label="E-Mail"
                        source="email"
                        validate={validateEmail}
                    />
                    <TextInput
                        label="Senha"
                        source="password"
                        validate={validadePassword}
                    />
                    <SelectInput
                        label="Empresa"
                        source="enterpriseId"
                        choices={enterpriseList}
                    />
                    <GroupedSelectInput
                        label="Permissões"
                        source="role"
                        multiple
                        groups={filterPermissionGroups([
                            'Customer',
                            'assistanceadmin',
                            'sysadmin',
                            'api',
                            'insuranceissuer',
                            'apimanager',
                            'assistanceleisure',
                            'assistancecorporate',
                            'markupmanager',
                        ])}
                    />
                    <div style={{ width: '100%' }}>
                        <p>
                            <strong>Cliente final:</strong> compra direta por
                            cliente final, sem acesso
                        </p>
                        <p>
                            <strong>Emissor Seguros:</strong> acesso apenas aos
                            seus próprios pedidos
                        </p>
                        <p>
                            <strong>Administrador de Seguros:</strong> acesso a
                            todos pedidos, sem acesso a outras seções
                        </p>
                        <p>
                            <strong>Administrador de Sistema:</strong> acesso a
                            todos pedidos, configurações e seções
                        </p>
                        <p>
                            <strong>Usuário API:</strong> Acesso a api, para
                            consumo e desenvolvimento
                        </p>
                        <p>
                            <strong>Gestor API:</strong> Acesso ao dash, para
                            gerenciar pedidos via api
                        </p>
                        <p>
                            <strong>Gestor de Markup:</strong> Acesso ao dash, para gerenciar somente markup de produtos
                        </p>
                    </div>
                    <TextInput label="Código Gate" source="agentCode" />
                    <BooleanInput label="Ativo" source="active" />
                </SimpleForm>
            </Create>
        </>
    )
}

export const UserEdit = (props) => {
    const { record } = useEditController(props)
    const [enterpriseList, setEnterpriseList] = React.useState([])

    useEffect(() => {
        ;(async () => {
            try {
                const results = await api.get(`admin/enterprises`, {
                    params: { issuer: true },
                })
                const empresasFormatted = results.data.map((result) => ({
                    id: result.id,
                    name: result.corporateName,
                }))
                setEnterpriseList(empresasFormatted)
            } catch (error) {
                console.log('Erro ao consultar empresas', error)
            }
        })()
    }, [])

    const TextInputCustom = (props) => (
        <TextInput {...props} style={{ width: 400 }} />
    )

    const transformData = (data) => ({
        ...data,
        Address: {
            cep: data['Address.cep'],
            address: data['Address.address'],
            complement: data['Address.complement'],
            number: data['Address.number'],
            district: data['Address.district'],
            city: data['Address.city'],
            state: data['Address.state'],
        },
    })

    return (
        <Edit {...props} transform={transformData}>
            <SimpleForm>
                <TextInputCustom label="Primeiro Nome" source="firstName" />
                <TextInputCustom label="Último nome" source="lastName" />
                <TextInputCustom
                    disabled={!record?.role?.includes('Customer')}
                    label="E-Mail"
                    source="email"
                />
                <TextInputCustom label="Data Nascimento" source="dob" />
                <TextInputCustom label="Telefone" source="phone" />
                <TextInputCustom label="Celular" source="mobile" />
                <TextInputCustom label="CEP" source="Address.cep" />
                <TextInputCustom label="Endereço" source="Address.address" />
                <TextInputCustom
                    label="Complemento"
                    source="Address.complement"
                />
                <TextInputCustom label="Numero" source="Address.number" />
                <TextInputCustom label="Bairro" source="Address.district" />
                <TextInputCustom label="Cidade" source="Address.city" />
                <TextInputCustom label="Estado" source="Address.state" />
                <TextInputCustom label="Código Gate" source="agentCode" />
                <SelectInput
                    label="Empresa"
                    source="enterpriseId"
                    choices={enterpriseList}
                />
                {/* <SelectInput label="Perfil" source="role" choices={[
                    { id: 'admin', name: 'Administrador' },
                    { id: 'user', name: 'Usuário' }
                ]} /> */}
                <GroupedSelectInput
                    label="Permissões"
                    source="role"
                    multiple
                    groups={filterPermissionGroups([
                        'Customer',
                        'assistanceadmin',
                        'sysadmin',
                        'api',
                        'insuranceissuer',
                        'apimanager',
                        'assistanceleisure',
                        'assistancecorporate',
                        'markupmanager',
                    ])}
                />
                <div style={{ width: '100%' }}>
                    <p>
                        <strong>Cliente final:</strong> compra direta por
                        cliente final, sem acesso
                    </p>
                    <p>
                        <strong>Emissor Seguros:</strong> acesso apenas aos seus
                        próprios pedidos
                    </p>
                    <p>
                        <strong>Administrador de Seguros:</strong> acesso a
                        todos pedidos, sem acesso a outras seções
                    </p>
                    <p>
                        <strong>Administrador de Sistema:</strong> acesso a
                        todos pedidos, configurações e seções
                    </p>
                    <p>
                        <strong>Usuário API:</strong> Acesso a api, para consumo
                        e desenvolvimento
                    </p>
                    <p>
                        <strong>Gestor API:</strong> Acesso ao dash, para
                        gerenciar pedidos via api
                    </p>
                    <p>
                        <strong>Gestor de Markup:</strong> Acesso ao dash, para
                        gerenciar somente markup de produtos
                    </p>
                </div>
                <BooleanInput label="Ativo" source="active" />
            </SimpleForm>
        </Edit>
    )
}

export const UserShow = (props) => {
    return (
        <Show {...props}>
            <SimpleShowLayout>
                <TextFieldRa label="E-Mail" source="email" />
                <TextFieldRa label="Código Gate" source="agentCode" />
                <TextFieldRa label="Data Criação" source="createdAt" />
                <SelectField
                    label="Perfil"
                    source="role"
                    choices={[
                        // { id: 'admin', name: 'Administrador' },
                        // { id: 'user', name: 'Usuário' },
                        { id: 'Customer', name: 'Cliente final' },
                        // { id: 'assistanceuser', name: 'Emissor Seguros' },
                        {
                            id: 'assistanceadmin',
                            name: 'Administrador Seguros',
                        },
                        {
                            id: 'sysadmin',
                            name: 'Administrador de Sistema (todas permissões)',
                        },
                        { id: 'api', name: 'Usuário API' },
                        {
                            id: 'insuranceissuer',
                            name: 'Emissor de Seguros B2C',
                        },
                        { id: 'apimanager', name: 'Gestor API' },
                        {
                            id: 'assistanceleisure',
                            name: 'Emissor de Seguros Lazer',
                        },
                        {
                            id: 'assistancecorporate',
                            name: 'Emissor de Seguros Corporativo',
                        },
                        {
                            id: 'markupmanager',
                            name: 'Gestor de Markup',
                        },
                    ]}
                />
            </SimpleShowLayout>
        </Show>
    )
}
